import React, { useCallback, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { useFocusEffect, useNavigation } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Button, Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { DietDto, DietService } from "../../../services/DietService";
import { useAuth } from "../../../../context/AuthContext";

export function DietChartScreen() {
    const navigation = useNavigation<any>();
    const { user } = useAuth();

    const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
    const [date, setDate] = useState(todayStr);

    const [dietId, setDietId] = useState<string | null>(null);
    const [morning, setMorning] = useState("");
    const [afternoon, setAfternoon] = useState("");
    const [night, setNight] = useState("");
    const [isEditable, setIsEditable] = useState(true);

    const [loading, setLoading] = useState(false);

    /* ---------------- RESET ---------------- */

    const resetForm = () => {
        setDietId(null);
        setMorning("");
        setAfternoon("");
        setNight("");
    };

    /* ---------------- LOAD DIET BY DATE ---------------- */

    const loadDietByDate = async (selectedDate: string) => {
        try {
            if (!user?.userId) return;

            setLoading(true);

            const res = await DietService.getDietAgainstDate({
                date: selectedDate,
            });

            if (res?.status !== 200) {
                resetForm();
                return;
            }

            const data = res?.data;

            if (!data) {
                // No entry found
                resetForm();
                return;
            }

            // Entry exists → set fields
            setDietId(data.dietId ?? null);
            setMorning(data.morning ?? "");
            setAfternoon(data.afternoon ?? "");
            setNight(data.night ?? "");
            setIsEditable(data.isEditable ?? true);

        } catch (e: any) {
            resetForm();
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- DATE PICKER ---------------- */

    const openDatePicker = () => {
        DateTimePickerAndroid.open({
            value: new Date(date),
            mode: "date",
            maximumDate: new Date(),
            display: "calendar",
            onChange: (event, selectedDate) => {
                if (event.type === "set" && selectedDate) {
                    const newDate = format(selectedDate, "yyyy-MM-dd");
                    setDate(newDate);

                    // 🔥 Load diet for selected date
                    loadDietByDate(newDate);
                }
            },
        });
    };

    /* ---------------- SAVE ---------------- */

    const onSave = async () => {
        try {
            if (!user?.userId) {
                Flash.error("User not found");
                return;
            }

            if (!morning.trim() && !afternoon.trim() && !night.trim()) {
                Flash.error("Please fill at least one meal");
                return;
            }

            setLoading(true);

            const payload: DietDto = {
                dietId: dietId ?? null,
                userId: user.userId,
                date,
                morning: morning?.trim() || null,
                afternoon: afternoon?.trim() || null,
                night: night?.trim() || null,
            };

            const res = dietId
                ? await DietService.update(payload)
                : await DietService.create(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to save");
                return;
            }

            Flash.success("Success!", "Diet Saved Successfully");

            // If newly created, backend should return DietId
            if (!dietId && res?.data?.dietId) {
                setDietId(res.data.dietId);
            }

            navigation.navigate("DietChartHistory");
        } catch (e: any) {
            Flash.error(e?.message || "Save failed");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- INITIAL LOAD ---------------- */

    useFocusEffect(
        useCallback(() => {
            loadDietByDate(date);
        }, [date])
    );

    /* ---------------- UI ---------------- */

    return (
        <DivineBackground overlayOpacity={0.92}>
            <View style={{ flex: 1 }}>
                <Header title="Diet Chart" showBack showProfile />

                <ScrollView contentContainerStyle={styles.container}>
                    <View style={styles.card}>
                        <View style={styles.topRow}>
                            <Pressable onPress={openDatePicker} style={styles.dateChip}>
                                <Text style={styles.dateChipIcon}>📅</Text>
                                <Text style={styles.dateChipText}>
                                    {format(new Date(date), "dd-MM-yyyy")}
                                </Text>
                            </Pressable>

                            <Pressable
                                onPress={() => navigation.navigate("DietChartHistory")}
                                style={styles.historyBtn}
                            >
                                <Text style={styles.historyIcon}>🕘</Text>
                                <Text style={styles.historyText}>History</Text>
                            </Pressable>
                        </View>

                        <View style={{ gap: 7, marginTop: 14 }}>
                            <Input
                                label="Morning"
                                placeholder="e.g. Fruits, Milk"
                                value={morning}
                                onChangeText={(text) => {
                                    if (text.length <= 1000) setMorning(text);
                                }}
                                multiline
                                numberOfLines={4}
                                textAlignVertical="top"
                                maxLength={1000}
                                inputStyle={{ height: 80 }}
                            />

                            <Text style={styles.charCount}>{morning.length}/1000</Text>

                            <Input
                                label="Noon"
                                placeholder="e.g. Prasadam (Rice, Dal)"
                                value={afternoon}
                                onChangeText={(text) => {
                                    if (text.length <= 1000) setAfternoon(text);
                                }}
                                multiline
                                numberOfLines={4}
                                textAlignVertical="top"
                                maxLength={1000}
                                inputStyle={{ height: 80 }}
                            />

                            <Text style={styles.charCount}>{afternoon.length}/1000</Text>

                            <Input
                                label="Night"
                                placeholder="e.g. Hot Milk"
                                value={night}
                                onChangeText={(text) => {
                                    if (text.length <= 1000) setNight(text);
                                }}
                                multiline
                                numberOfLines={4}
                                textAlignVertical="top"
                                maxLength={1000}
                                inputStyle={{ height: 80 }}
                            />

                            <Text style={styles.charCount}>{night.length}/1000</Text>
                        </View>

                        {isEditable && (
                            <View style={styles.btnRow}>
                                <Pressable
                                    onPress={() => navigation.goBack()}
                                    style={styles.cancelBtn}
                                    disabled={loading}
                                >
                                    <Text style={styles.cancelText}>Cancel</Text>
                                </Pressable>

                                <View style={{ flex: 1 }}>
                                    <Button
                                        title={loading ? "Saving..." : "Save"}
                                        onPress={onSave}
                                        disabled={loading}
                                    />
                                </View>
                            </View>
                        )}

                        {loading && (
                            <View style={{ marginTop: 12, alignItems: "center" }}>
                                <ActivityIndicator />
                            </View>
                        )}
                    </View>
                </ScrollView>
            </View>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    container: { padding: 14, paddingBottom: 30 },

    card: {
        borderRadius: 16,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.88)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 8 },
        elevation: 4,
    },

    topRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    dateChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(229,247,238,0.85)",
        borderWidth: 1,
        borderColor: "rgba(16,185,129,0.28)",
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
    },

    dateChipIcon: { fontSize: 14 },
    dateChipText: {
        fontSize: 13,
        fontWeight: "800",
        color: "#1a4731",
    },

    historyBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 10,
        height: 34,
        borderRadius: 10,
        backgroundColor: "rgba(255,255,255,0.55)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
    },

    historyIcon: { fontSize: 14 },
    historyText: {
        fontSize: 12,
        fontWeight: "800",
        color: "rgba(26,71,49,0.78)",
    },

    charCount: {
        fontSize: 11,
        color: "rgba(0,0,0,0.45)",
        textAlign: "right",
        marginTop: -6,
    },

    btnRow: { flexDirection: "row", gap: 10, marginTop: 14 },

    cancelBtn: {
        flex: 1,
        height: 46,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.12)",
        backgroundColor: "rgba(255,255,255,0.9)",
        alignItems: "center",
        justifyContent: "center",
    },

    cancelText: {
        fontSize: 14,
        fontWeight: "900",
        color: "rgba(26,71,49,0.60)",
    },
});